// filepath: src/pages/LoginPage.js
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";
import Footer from "../components/CommonComponents/Footer";
import "../index.css";
import Lottie from "lottie-react";
import animationData from "../assets/Advanced-analytics.json";
import { getTenantLoginAccent } from "../constants/loginTheme";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const loginAccent = getTenantLoginAccent();

  // Determine role and login title based on route (no superadmin logic)
  let role = "pos";
  let loginTitle = "Login";
  if (location.pathname.includes("/admin/login")) {
    role = "admin";
    loginTitle = "Admin Login";
  }

  // Handle login submit
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Please enter both username and password");
      return;
    }
    const success = await login({ username, password }, role);
    if (success) {
      if (role === "admin") navigate("/admin/dashboard");
      else navigate("/dashboard");
    }
  };

  return (
    <div className="flex min-h-screen bg-[#f7f8fa]">
      {/* Left: Login Form */}
      <div className="flex flex-col justify-center items-center w-full md:w-[55%] px-4 transition-all duration-300">
        <div className="w-full max-w-sm bg-white p-8 rounded-md shadow border border-gray-100">
          <h2 className="text-center text-xl font-semibold mb-6" style={{ color: "#0f172a" }}>{loginTitle}</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <FaUser className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: loginAccent }} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Username"
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full text-sm focus:outline-none focus:ring-2"
                style={{ color: '#111', boxShadow: `0 0 0 2px transparent`, caretColor: loginAccent }}
                autoComplete="username"
                onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${loginAccent}`}
                onBlur={e => e.target.style.boxShadow = `0 0 0 2px transparent`}
              />
            </div>
            <div className="relative">
              <FaLock className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: loginAccent }} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Password"
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md w-full text-sm focus:outline-none focus:ring-2"
                style={{ color: '#111', boxShadow: `0 0 0 2px transparent`, caretColor: loginAccent }}
                autoComplete="current-password"
                onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${loginAccent}`}
                onBlur={e => e.target.style.boxShadow = `0 0 0 2px transparent`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <p className="text-xs text-gray-600 text-center">
              Use your credentials to access the system.
            </p>
            <button
              type="submit"
              className="w-full font-semibold py-2 rounded-md transition text-sm"
              style={{ background: loginAccent, color: '#fff' }}
            >
              Log In
            </button>
          </form>
        </div>
      </div>
      {/* Right: Animation & Description */}
      <div className="hidden md:flex flex-col justify-center items-center w-[45%] bg-[#f7f8fa] px-8 transition-all duration-300">
        <div className="w-[340px] h-[240px] mb-4 flex items-center justify-center">
          <Lottie animationData={animationData} loop={true} className="w-full h-full object-contain" />
        </div>
        <div className="max-w-sm w-full mt-1 px-8 py-6 border border-gray-200 rounded-sm bg-white/70">
          <p
            className="text-[11px] uppercase tracking-[0.28em] text-center text-gray-500 mb-3"
            style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
          >
            ZeroOne Standard
          </p>
          <p
            className="text-center text-[1.15rem] leading-relaxed text-slate-800"
            style={{ fontFamily: "ui-serif, Georgia, Cambria, 'Times New Roman', serif" }}
          >
            Turn Every Transaction Into Insight.
          </p>
          <div className="mt-4 h-px w-16 mx-auto bg-gray-300" />
        </div>
      </div>
      {/* Footer */}
      <Footer
        className="text-xs text-gray-500 py-4 absolute bottom-0 w-full"
        noBorder
        noPadding
        showLegalLinks
      />
    </div>
  );
}
